/**
 * attendanceSummaryModel.js — the Attendance Summary report's pure model
 * (PRD_AttendanceSummaryReport C8/C10/C11/C12).
 *
 * PURE: no jsPDF, no Firestore, no Date.now() — every clock-derived value
 * (generated-at, the coverage stamp) arrives inside the buildDtrModel outputs
 * this consumes, which is where the Manila derivation already lives (C4).
 * Same split as dtrModel.js + dtrPdfService.js: everything assertable is
 * here; attendanceSummaryPdfService.js only renders it.
 *
 * The vertical page constants live HERE, not in the drawing layer, because
 * paginateRows needs them and importing the drawing layer would pull jsPDF
 * and the font blob into a pure module. The drawing layer imports them.
 */
import { FUTURE_RANGE_NOTICE } from './dtrModel';

// ── Vertical geometry (C8) — mm, portrait A4 ──
// Sized for 9pt body type (2026-10-11, up from 7pt for printed legibility):
// an 8mm margin, not the DTR's 12, buys the width the larger columns need.
export const PAGE_W = 210;
export const PAGE_H = 297;
export const MARGIN = 8;
export const CONTENT_W = PAGE_W - MARGIN * 2; // 194
export const ROW_H = 7.2;
export const SUMMARY_HEADER_H = 26; // company, title, period, generated-at, scope, stamp
export const SUMMARY_COL_HDR_H = 12.3; // the two-line ("stacked") column band
const FOOT_LIMIT = PAGE_H - MARGIN; // 289 — content stays above this

// Derived, not asserted: 33 on page 1, 37 on later pages (C8).
export const ROWS_PER_FIRST_PAGE = Math.floor(
  (FOOT_LIMIT - (MARGIN + SUMMARY_HEADER_H + SUMMARY_COL_HDR_H)) / ROW_H
);
export const ROWS_PER_LATER_PAGE = Math.floor((FOOT_LIMIT - (MARGIN + SUMMARY_COL_HDR_H)) / ROW_H);

/**
 * The ONLY copy of the page-break arithmetic (C8): one pageIndex per row.
 * Lives in the pure model so "34 rows → two pages" is a Tier-1 assertion;
 * the drawing layer obeys these, and its own test only re-checks page count
 * against them.
 *
 * @param {number} rowCount
 * @returns {number[]} zero-based page index per row
 */
export const paginateRows = (rowCount) => {
  const pages = [];
  for (let i = 0; i < rowCount; i++) {
    pages.push(
      i < ROWS_PER_FIRST_PAGE
        ? 0
        : 1 + Math.floor((i - ROWS_PER_FIRST_PAGE) / ROWS_PER_LATER_PAGE)
    );
  }
  return pages;
};

/** C10: an identity field still empty after buildDtrModel's fallbacks. */
const EMPTY_CELL = '—';
const orDash = (v) => (v ? String(v) : EMPTY_CELL);

/**
 * buildDtrModel's 2dp hour strings with trailing zeros dropped: '176.00' →
 * '176', '174.60' → '174.6', '0.12' stays. Hours are minute-precise, so the
 * second decimal is kept wherever it carries a value.
 */
const trimHours = (v) => String(Number(v));

/**
 * C23: the Sort by choices — every column, labelled as its printed heading,
 * in column order. Lives here (not beside COLS) so the page can list them
 * without loading the drawing layer.
 */
export const SORT_OPTIONS = [
  { key: 'employeeId', label: 'ID' },
  { key: 'employeeName', label: 'Name' },
  { key: 'department', label: 'Department' },
  { key: 'daysWorked', label: 'Days' },
  { key: 'pieceRateDays', label: 'Piece Rate' },
  { key: 'absentDays', label: 'Absent' },
  { key: 'leaveDays', label: 'Leave' },
  { key: 'lateDays', label: 'Late' },
  { key: 'totalRegularHours', label: 'Regular Hours' },
  { key: 'totalOTHours', label: 'OT Hours' },
  { key: 'totalNightDiff', label: 'Night Diff' },
  { key: 'totalUtHours', label: 'UT Hours' },
  { key: 'totalBreakHours', label: 'Break' },
];
const TEXT_SORT_KEYS = new Set(['employeeId', 'employeeName', 'department']);

/**
 * C23: ID, Name and Department compare with localeCompare (a raw < / > ranks
 * Ñ above every plain letter — EmployeeList.js does the same); every other
 * column compares as a number, so 9 sorts before 72. Array.prototype.sort is
 * stable, so ties keep the incoming (employeeId) order in both directions.
 */
const compareRows = (key, direction) => (a, b) => {
  const c = TEXT_SORT_KEYS.has(key)
    ? String(a[key]).localeCompare(String(b[key]))
    : Number(a[key]) - Number(b[key]);
  return direction === 'desc' ? -c : c;
};

/**
 * Build the summary model from one buildDtrModel output per employee.
 *
 * Row figures are buildDtrModel's `totals` (ten, C7) and identity fields its
 * `header` — never the raw employee document, so the DTR report's fallbacks
 * (fullName → first + last) are reused rather than forked (C10). Range-scoped
 * values (period label, generated-at, coverage stamp, file stamp) are lifted
 * off the first model; they are identical across employees by construction.
 *
 * @param {Object} args
 * @param {Object} args.company - company doc; header prints `name` only
 * @param {Object[]} args.models - buildDtrModel outputs, one per employee, in
 *   roster order (the page sorts by employeeId); length ≥ 1
 * @param {string} args.rangeStartYMD - 'YYYY-MM-DD'
 * @param {string} args.rangeEndYMD - 'YYYY-MM-DD'
 * @param {string} args.scopeLabel - e.g. 'All employees'
 * @param {{key: string, direction: 'asc'|'desc'}} [args.sort] - C23; rows are
 *   sorted BEFORE pagination, so page 2 continues the order
 * @returns {{header: Object, rows: Object[], coverageStamp: string|null, pageCount: number}}
 */
export const buildAttendanceSummaryModel = ({
  company,
  models,
  rangeStartYMD,
  rangeEndYMD,
  scopeLabel,
  sort = { key: 'employeeId', direction: 'asc' },
}) => {
  const first = models[0];
  const coverageStamp = first.partialStamp;
  // C12: the future notice REPLACES the table body (overriding C11's zero rows).
  const isFuture = coverageStamp === FUTURE_RANGE_NOTICE;

  const pageIndexes = paginateRows(isFuture ? 0 : models.length);
  const rows = isFuture
    ? []
    : models
      .map((m) => ({
        // C6's column order.
        employeeId: orDash(m.header.employeeId),
        employeeName: orDash(m.header.employeeName),
        department: orDash(m.header.department),
        daysWorked: m.totals.daysWorked,
        pieceRateDays: m.totals.pieceRateDays,
        absentDays: m.totals.absentDays,
        leaveDays: m.totals.leaveDays,
        lateDays: m.totals.lateDays,
        totalRegularHours: trimHours(m.totals.totalRegularHours),
        totalOTHours: trimHours(m.totals.totalOTHours),
        totalNightDiff: trimHours(m.totals.totalNightDiff),
        totalUtHours: trimHours(m.totals.totalUtHours),
        totalBreakHours: trimHours(m.totals.totalBreakHours),
      }))
      .sort(compareRows(sort.key, sort.direction))
      .map((row, i) => ({ ...row, pageIndex: pageIndexes[i] }));

  return {
    header: {
      companyName: company?.name || '',
      periodLabel: first.header.periodLabel,
      periodStart: rangeStartYMD,
      periodEnd: rangeEndYMD,
      scopeLabel,
      generatedAt: first.header.generatedAt,
      fileStamp: first.header.fileStamp,
    },
    rows,
    coverageStamp,
    pageCount: rows.length === 0 ? 1 : rows[rows.length - 1].pageIndex + 1,
  };
};
